<template>
  <div class="wg-page">

    <!-- ── HEADER ── -->
    <div class="wg-header">
      <h1 class="wg-title">Word Generator</h1>
      <p class="wg-subtitle">Generate a word, hide it, then try to spell it from memory.</p>
    </div>

    <!-- ── SCORE BAR ── -->
    <div class="wg-scorebar">
      <div class="score-pill score-pill--correct">
        <span class="score-pill__num">{{ score.correct }}</span>
        <span class="score-pill__label">Correct</span>
      </div>
      <div class="score-pill score-pill--streak" v-if="score.streak > 1">
        <span class="score-pill__num">🔥 {{ score.streak }}</span>
        <span class="score-pill__label">Streak</span>
      </div>
      <div class="score-pill score-pill--wrong">
        <span class="score-pill__num">{{ score.wrong }}</span>
        <span class="score-pill__label">Wrong</span>
      </div>
      <button class="score-reset" @click="resetScore" title="Reset score">↺</button>
    </div>

    <!-- ── DIFFICULTY FILTER ── -->
    <div class="wg-difficulty">
      <button
        v-for="d in difficulties"
        :key="d.key"
        class="diff-btn"
        :class="{ 'diff-btn--active': difficulty === d.key }"
        @click="setDifficulty(d.key)"
      >{{ d.label }}</button>
    </div>

    <!-- ── WORD CARD ── -->
    <div class="wg-card">

      <!-- Word display area -->
      <transition name="wg-pop" mode="out-in">
        <div class="wg-word-wrap" :key="currentWord">
          <div v-if="currentWord && !wordHidden" class="wg-word">{{ currentWord.toUpperCase() }}</div>
          <div v-else-if="currentWord && wordHidden" class="wg-word wg-word--hidden">
            <span v-for="(ch, i) in currentWord" :key="i" class="wg-blank">{{ hintRevealed > i ? ch.toUpperCase() : '_' }}</span>
          </div>
          <div v-else class="wg-word-placeholder">Press <strong>Generate</strong> to get a word</div>
        </div>
      </transition>

      <!-- Word length badge -->
      <div class="wg-meta" v-if="currentWord">
        <span class="wg-badge">{{ currentWord.length }} letters</span>
        <span class="wg-badge wg-badge--diff">{{ difficultyLabel }}</span>
      </div>

      <!-- Controls row -->
      <div class="wg-controls">
        <button class="wg-btn wg-btn--primary" @click="generateWord">
          {{ currentWord ? '↻ New Word' : '✦ Generate' }}
        </button>
        <button
          class="wg-btn wg-btn--secondary"
          v-if="currentWord && !wordHidden"
          @click="hideWord"
        >👁 Hide &amp; Spell</button>
        <button
          class="wg-btn wg-btn--hint"
          v-if="wordHidden && feedback === null"
          :disabled="hintRunning"
          @click="startHint"
        >💡 Hint</button>
        <button
          class="wg-btn wg-btn--secondary"
          v-if="wordHidden"
          @click="wordHidden = false"
        >👁 Reveal Word</button>
      </div>

      <!-- Hint progress bar -->
      <transition name="wg-slide">
        <div class="wg-hint-bar-wrap" v-if="hintRunning">
          <div class="wg-hint-bar-track">
            <div
              class="wg-hint-bar-fill"
              :style="{ width: hintProgress + '%' }"
            ></div>
          </div>
          <span class="wg-hint-bar-label">Revealing… try again when done</span>
        </div>
      </transition>

      <!-- Spell-check input (shown after word is hidden) -->
      <transition name="wg-slide">
        <div class="wg-spell-area" v-if="wordHidden">
          <label class="wg-spell-label">Type the word you just saw:</label>
          <div class="wg-spell-row">
            <input
              ref="spellInput"
              class="wg-spell-input"
              :class="{
                'wg-spell-input--correct': feedback === 'correct',
                'wg-spell-input--wrong': feedback === 'wrong'
              }"
              type="text"
              v-model="userSpelling"
              placeholder="Spell it here…"
              @keyup.enter="checkSpelling"
              :disabled="feedback !== null"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
            />
            <button
              class="wg-btn wg-btn--check"
              @click="checkSpelling"
              :disabled="!userSpelling.trim() || feedback !== null"
            >Check</button>
          </div>

          <!-- Feedback -->
          <transition name="wg-fade">
            <div v-if="feedback === 'correct'" class="wg-feedback wg-feedback--correct">
              ✅ Correct! Well done.
            </div>
            <div v-else-if="feedback === 'wrong'" class="wg-feedback wg-feedback--wrong">
              ❌ Not quite - the word was <strong>{{ currentWord }}</strong>
            </div>
          </transition>
        </div>
      </transition>

    </div>

    <!-- ── WORD HISTORY ── -->
    <div class="wg-history" v-if="history.length">
      <h3 class="wg-history__title">This Session</h3>
      <div class="wg-history__list">
        <span
          v-for="(entry, i) in history"
          :key="i"
          class="wg-history__tag"
          :class="{
            'wg-history__tag--correct': entry.result === 'correct',
            'wg-history__tag--wrong': entry.result === 'wrong',
            'wg-history__tag--skipped': entry.result === 'skipped'
          }"
          :title="entry.result"
        >{{ entry.word }}</span>
      </div>
    </div>

  </div>
</template>

<script>
var randomWords = require("random-words");

const DIFFICULTIES = [
  { key: "easy",   label: "Easy (1–5)",   min: 1,  max: 5  },
  { key: "medium", label: "Medium (6–8)", min: 6,  max: 8  },
  { key: "hard",   label: "Hard (9+)",    min: 9,  max: 30 },
  { key: "any",    label: "Any",          min: 1,  max: 30 },
];

export default {
  name: "WordGenerator",

  data() {
    return {
      difficulties: DIFFICULTIES,
      difficulty: "any",

      currentWord: "",
      wordHidden: false,
      hintRevealed: 0,   // number of letters currently revealed by hint

      // hint timer state
      hintRunning: false,
      hintProgress: 100, // 100 → 0 as bar drains
      hintInterval: null,

      userSpelling: "",
      feedback: null,     // null | 'correct' | 'wrong'

      score: {
        correct: 0,
        wrong: 0,
        streak: 0,
      },

      history: [],         // [{ word, result }]
    };
  },

  computed: {
    difficultyLabel() {
      const d = this.difficulties.find((d) => d.key === this.difficulty);
      return d ? d.label : "";
    },
  },

  methods: {
    setDifficulty(key) {
      this.difficulty = key;
      if (this.currentWord) this.pushHistory("skipped");
      this.resetRound();
    },

    generateWord() {
      if (this.currentWord && this.wordHidden && this.feedback === null) {
        this.pushHistory("skipped");
      }
      this.stopHint();

      const d = this.difficulties.find((d) => d.key === this.difficulty);
      let word = "";
      let attempts = 0;
      do {
        word = randomWords();
        attempts++;
        if (attempts > 200) break;
      } while (word.length < d.min || word.length > d.max);

      this.currentWord = word;
      this.wordHidden = false;
      this.userSpelling = "";
      this.feedback = null;
      this.hintRevealed = 0;
    },

    hideWord() {
      this.wordHidden = true;
      this.userSpelling = "";
      this.feedback = null;
      this.hintRevealed = 0;
      this.stopHint();
      this.$nextTick(() => {
        if (this.$refs.spellInput) this.$refs.spellInput.focus();
      });
    },

    // ── Hint: timed progress bar that reveals letters as it drains ──
    startHint() {
      if (this.hintRunning || !this.currentWord) return;

      // Reset spell input so the user gets a clean attempt at the end
      this.userSpelling = "";
      this.feedback = null;
      this.hintRevealed = 0;
      this.hintRunning = true;
      this.hintProgress = 100;

      const totalMs = 5000;          // total hint duration
      const tickMs  = 80;            // how often we update
      const steps   = totalMs / tickMs;
      const wordLen = this.currentWord.length;

      let tick = 0;
      this.hintInterval = setInterval(() => {
        tick++;
        this.hintProgress = Math.max(0, 100 - (tick / steps) * 100);

        // Reveal one letter at a time proportionally across the bar drain
        const shouldReveal = Math.floor((tick / steps) * wordLen);
        this.hintRevealed = Math.min(shouldReveal, wordLen);

        if (tick >= steps) {
          // Bar finished - hide again and let user try
          this.stopHint();
          this.hintRevealed = 0;
          this.userSpelling = "";
          this.$nextTick(() => {
            if (this.$refs.spellInput) this.$refs.spellInput.focus();
          });
        }
      }, tickMs);
    },

    stopHint() {
      if (this.hintInterval) {
        clearInterval(this.hintInterval);
        this.hintInterval = null;
      }
      this.hintRunning  = false;
      this.hintProgress = 100;
    },

    checkSpelling() {
      if (!this.userSpelling.trim() || this.feedback !== null) return;

      const isCorrect =
        this.userSpelling.trim().toLowerCase() ===
        this.currentWord.toLowerCase();

      this.feedback = isCorrect ? "correct" : "wrong";

      if (isCorrect) {
        this.score.correct++;
        this.score.streak++;
        this.pushHistory("correct");
      } else {
        this.score.wrong++;
        this.score.streak = 0;
        this.pushHistory("wrong");
      }
    },

    pushHistory(result) {
      if (!this.currentWord) return;
      this.history.unshift({ word: this.currentWord, result });
      if (this.history.length > 20) this.history.pop();
    },

    resetRound() {
      this.stopHint();
      this.currentWord = "";
      this.wordHidden = false;
      this.userSpelling = "";
      this.feedback = null;
      this.hintRevealed = 0;
    },

    resetScore() {
      this.score = { correct: 0, wrong: 0, streak: 0 };
      this.history = [];
      this.resetRound();
    },
  },

  beforeDestroy() {
    this.stopHint();
  },
};
</script>

<style scoped>
/* ── Page shell ── */
.wg-page {
  max-width: 640px;
  margin: 0 auto;
  padding: 1rem 1rem 3rem;
}

/* ── Header ── */
.wg-header {
  text-align: center;
  margin-bottom: 1.25rem;
}
.wg-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--color-primary-dark, #0f5c52);
  margin: 0 0 0.3rem;
}
.wg-subtitle {
  font-size: 0.95rem;
  color: var(--color-text-muted, #64748b);
  margin: 0;
}

/* ── Score bar ── */
.wg-scorebar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 1.1rem;
  flex-wrap: wrap;
}
.score-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 64px;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-weight: 700;
  line-height: 1.2;
}
.score-pill__num  { font-size: 1.25rem; }
.score-pill__label{ font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.8; }
.score-pill--correct { background: #d1fae5; color: #065f46; }
.score-pill--wrong   { background: #fee2e2; color: #991b1b; }
.score-pill--streak  { background: #fff7ed; color: #c2410c; }
.score-reset {
  background: none;
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  font-size: 1rem;
  cursor: pointer;
  color: var(--color-text-muted, #64748b);
  line-height: 1;
  transition: background 0.15s;
}
.score-reset:hover { background: var(--color-primary-light, #ccfbf1); }

/* ── Difficulty ── */
.wg-difficulty {
  display: flex;
  justify-content: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}
.diff-btn {
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  border: 1.5px solid var(--color-border, #e2e8f0);
  background: var(--color-surface, #fff);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text, #1e293b);
  cursor: pointer;
  transition: all 0.15s;
}
.diff-btn:hover { border-color: var(--color-primary, #0d9488); color: var(--color-primary-dark, #0f5c52); }
.diff-btn--active {
  background: var(--color-primary, #0d9488);
  border-color: var(--color-primary, #0d9488);
  color: #fff;
}

/* ── Card ── */
.wg-card {
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: var(--radius-lg, 1rem);
  box-shadow: var(--shadow-md, 0 4px 16px rgba(0,0,0,.07));
  padding: 2rem 1.5rem 1.75rem;
  margin-bottom: 1.5rem;
}

/* ── Word display ── */
.wg-word-wrap { min-height: 90px; display: flex; align-items: center; justify-content: center; }
.wg-word {
  font-size: clamp(2.2rem, 8vw, 3.5rem);
  font-weight: 800;
  color: var(--color-primary-dark, #0f5c52);
  text-align: center;
  letter-spacing: 0.05em;
  word-break: break-all;
}
.wg-word--hidden {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  justify-content: center;
  font-size: clamp(1.8rem, 7vw, 3rem);
}
.wg-blank {
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  min-width: 1.6rem;
  border-bottom: 3px solid var(--color-primary, #0d9488);
  color: var(--color-primary-dark, #0f5c52);
  font-weight: 800;
  padding-bottom: 2px;
}
.wg-word-placeholder {
  font-size: 1.05rem;
  color: var(--color-text-muted, #94a3b8);
  text-align: center;
}

/* ── Meta badges ── */
.wg-meta {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.6rem 0 1.1rem;
}
.wg-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: var(--color-primary-light, #ccfbf1);
  color: var(--color-primary-dark, #0f5c52);
}
.wg-badge--diff { background: #ede9fe; color: #5b21b6; }

/* ── Controls ── */
.wg-controls {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.25rem;
}
.wg-btn {
  padding: 0.5rem 1.2rem;
  border-radius: var(--radius-sm, 0.5rem);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.wg-btn--primary {
  background: var(--color-primary, #0d9488);
  color: #fff;
}
.wg-btn--primary:hover { background: var(--color-primary-dark, #0f5c52); }
.wg-btn--secondary {
  background: var(--color-surface, #fff);
  color: var(--color-primary-dark, #0f5c52);
  border: 1.5px solid var(--color-primary, #0d9488);
}
.wg-btn--secondary:hover { background: var(--color-primary-light, #ccfbf1); }
.wg-btn--hint {
  background: #fff7ed;
  color: #c2410c;
  border: 1.5px solid #fed7aa;
}
.wg-btn--hint:hover { background: #ffedd5; }
.wg-btn--check {
  background: #1d4ed8;
  color: #fff;
  white-space: nowrap;
}
.wg-btn--check:hover:not(:disabled) { background: #1e40af; }
.wg-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Hint progress bar ── */
.wg-hint-bar-wrap {
  margin-top: 0.9rem;
  text-align: center;
}
.wg-hint-bar-track {
  height: 8px;
  background: #fed7aa;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.4rem;
}
.wg-hint-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #f97316, #fb923c);
  border-radius: 999px;
  transition: width 0.08s linear;
}
.wg-hint-bar-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #c2410c;
}

/* ── Spell area ── */
.wg-spell-area {
  margin-top: 1.25rem;
  border-top: 1px solid var(--color-border, #e2e8f0);
  padding-top: 1.1rem;
}
.wg-spell-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-muted, #64748b);
  margin-bottom: 0.55rem;
  text-align: center;
}
.wg-spell-row {
  display: flex;
  gap: 0.5rem;
}
.wg-spell-input {
  flex: 1;
  padding: 0.6rem 0.9rem;
  border-radius: var(--radius-sm, 0.5rem);
  border: 1.5px solid var(--color-border, #e2e8f0);
  font-size: 1.05rem;
  font-weight: 600;
  outline: none;
  transition: border-color 0.15s;
  background: var(--color-surface, #fff);
  color: var(--color-text, #1e293b);
}
.wg-spell-input:focus { border-color: var(--color-primary, #0d9488); }
.wg-spell-input--correct { border-color: #16a34a; background: #f0fdf4; }
.wg-spell-input--wrong   { border-color: #dc2626; background: #fef2f2; }

/* ── Feedback ── */
.wg-feedback {
  margin-top: 0.75rem;
  padding: 0.6rem 1rem;
  border-radius: var(--radius-sm, 0.5rem);
  font-size: 0.95rem;
  font-weight: 600;
  text-align: center;
}
.wg-feedback--correct { background: #d1fae5; color: #065f46; }
.wg-feedback--wrong   { background: #fee2e2; color: #991b1b; }

/* ── History ── */
.wg-history {
  margin-top: 0.5rem;
}
.wg-history__title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted, #94a3b8);
  font-weight: 700;
  margin: 0 0 0.6rem;
  text-align: center;
}
.wg-history__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: center;
}
.wg-history__tag {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  background: var(--color-surface, #fff);
  border: 1.5px solid var(--color-border, #e2e8f0);
  color: var(--color-text, #1e293b);
}
.wg-history__tag--correct { background: #d1fae5; border-color: #6ee7b7; color: #065f46; }
.wg-history__tag--wrong   { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
.wg-history__tag--skipped { opacity: 0.5; }

/* ── Transitions ── */
.wg-pop-enter-active  { animation: wg-pop-in  0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
.wg-pop-leave-active  { animation: wg-pop-out 0.15s ease-in; }
@keyframes wg-pop-in  { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
@keyframes wg-pop-out { from { opacity: 1; transform: scale(1);    } to { opacity: 0; transform: scale(0.85); } }

.wg-slide-enter-active { transition: all 0.25s ease; }
.wg-slide-leave-active { transition: all 0.2s ease; }
.wg-slide-enter, .wg-slide-leave-to { opacity: 0; transform: translateY(-8px); }

.wg-fade-enter-active, .wg-fade-leave-active { transition: opacity 0.2s; }
.wg-fade-enter, .wg-fade-leave-to { opacity: 0; }

/* ── Responsive ── */
@media (max-width: 480px) {
  .wg-card { padding: 1.25rem 1rem 1.25rem; }
  .wg-spell-row { flex-direction: column; }
  .wg-btn--check { width: 100%; }
}
</style>
